import { makeStyles } from '@material-ui/core/styles';
import { spacingSizesMap, SecondaryColors, TextColors, TextOpacities } from '@/utils/constants';
import { getVerticalSpacing, hexToRGBA } from '@/utils/helpers';

const lightTheme = {
  root: {
    '& $title': {
      color: SecondaryColors.dark,
    },

    '& $content': {
      color: TextColors.dark,
    },

    '& $tabScrollable': {
      '&:before': {
        borderLeft: `1px solid ${hexToRGBA(TextColors.dark, TextOpacities.light3)}`,
      },

      '& .tab-content': {
        '&:before': {
          borderLeft: `3px solid ${SecondaryColors.dark}`,
        },
      },
    },

    '& $tabs': {
      '&:not(.MuiTabs-vertical):before': {
        borderBottom: `1px solid ${hexToRGBA(TextColors.dark, TextOpacities.light3)}`,
      },
      '&.MuiTabs-vertical:before': {
        borderBottom: `1px solid ${hexToRGBA(TextColors.dark, TextOpacities.light3)}`,
      },
      '& .MuiTabs-scroller .MuiTabs-indicator': {
        backgroundColor: SecondaryColors.dark,
      },
    },

    '& $tab': {
      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.dark, TextOpacities.light2),

        '&.Mui-selected, &:hover': {
          color: TextColors.dark,
        },
      },
    },

    '& $rtc': {
      color: hexToRGBA(TextColors.dark, TextOpacities.light2),

      '& b, & strong': {
        color: TextColors.dark,
      },
    },
  },
};
const darkTheme = {
  root: {
    '& $title': {
      color: SecondaryColors.main,
    },

    '& $content': {
      color: TextColors.light,
    },

    '& $tabScrollable': {
      '&:before': {
        borderLeft: `1px solid ${hexToRGBA(TextColors.light, TextOpacities.dark3)}`,
      },

      '& .tab-content': {
        '&:before': {
          borderLeft: `3px solid ${SecondaryColors.main}`,
        },
      },
    },

    '& $tabs': {
      '&:not(.MuiTabs-vertical):before': {
        borderBottom: `1px solid ${hexToRGBA(TextColors.light, TextOpacities.dark3)}`,
      },
      '&.MuiTabs-vertical:before': {
        borderBottom: `1px solid ${hexToRGBA(TextColors.light, TextOpacities.dark3)}`,
      },
      '& .MuiTabs-scroller .MuiTabs-indicator': {
        backgroundColor: SecondaryColors.main,
      },
    },

    '& $tab': {
      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.light, TextOpacities.dark2),

        '&.Mui-selected, &:hover': {
          color: TextColors.light,
        },
      },
    },

    '& $rtc': {
      color: hexToRGBA(TextColors.light, TextOpacities.dark2),

      '& b, & strong': {
        color: TextColors.light,
      },
    },
  },
};

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      '&.light-theme': lightTheme.root,
      '&.dark-theme': darkTheme.root,
      ...getVerticalSpacing(muiTheme, '& .ctl-vertical-content-featured'),
    },

    title: {
      '&.MuiTypography-root': {
        marginBottom: '16px',
      },
    },

    featured: {
      [muiTheme.breakpoints.down('sm')]: {
        marginBottom: muiTheme.spacing(4),
      },

      [muiTheme.breakpoints.up('md')]: {
        marginBottom: spacingSizesMap.medium,
      },

      '& > * + *': {
        marginTop: muiTheme.spacing(2),
      },
    },

    content: {
      [muiTheme.breakpoints.up('md')]: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        alignContent: 'center',
      },

      '& .tabs': {
        position: 'relative',

        [muiTheme.breakpoints.up('md')]: {
          flex: '1 1 40%',
          marginRight: '40px',
          order: 1,
        },
      },
      '& .tabPanels': {
        position: 'relative',
        [muiTheme.breakpoints.down('sm')]: {
          position: 'static !important',
          marginBottom: '30px',
        },

        [muiTheme.breakpoints.up('md')]: {
          position: 'sticky',
          flex: '1 1 60%',
          marginLeft: '40px',
          order: 2,
          alignSelf: 'flex-start',
          top: '100px',
        },
        '&.vertical-featured-sticky': {
          [muiTheme.breakpoints.up('md')]: {
            top: 'calc(50% - 120px)',
          },
        },
      },
    },

    scrollable: {
      '&:nth-child(2)': {},
    },

    tabScrollable: {
      position: 'relative',
      padding: '2px 2px 2px 18px',

      '&:before': {
        content: '""',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: '-1px',
      },

      '& .tab-content': {
        margin: muiTheme.spacing(-0.25, -0.25, 8, 0),
        transition: 'all 0.3s ease-in-out',

        '&:before': {
          content: '""',
          height: '28px',
          position: 'absolute',
          top: 0,
          left: '-2px',
        },
      },

      '&:not([aria-selected="true"]) .tab-content': {
        opacity: 0.5,

        '&:before': {
          opacity: 0,
        },
      },
    },

    tabs: {
      position: 'relative',

      '&:not(.MuiTabs-vertical)': {
        display: 'inline-flex',
        padding: '2px 2px 0px 0px',
        margin: '-2px -2px 0px -32px',
        width: 'calc(100% + 64px)',

        '&:before': {
          content: '""',
          width: 'calc(100% - 64px)',
          position: 'absolute',
          top: 0,
          left: '32px',
          bottom: 0,
        },

        '& .MuiTabs-scrollButtons': {
          width: '32px',
        },

        '& ~ *': {
          marginTop: '28px',
        },
      },

      '&.MuiTabs-vertical': {
        padding: '2px 2px 2px 0',
        margin: '-2px -2px -2px 0',

        '&:before': {
          content: '""',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
        },

        '& .MuiTabs-indicator': {
          height: '28px !important',
          left: 0,
          zIndex: 1,
        },

        '& .MuiTabs-flexContainer': {
          paddingLeft: '6px',
        },

        '& .MuiTabs-fixed': {
          padding: '2px 2px 2px 0',
          margin: '-2px -2px -2px 0',
        },
      },
    },

    tab: {
      '& .MuiTab-root': {
        [muiTheme.breakpoints.down('sm')]: {
          maxWidth: '300px',
        },

        [muiTheme.breakpoints.up('md')]: {
          flexDirection: 'column',
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          justifyContent: 'flex-start',
          textAlign: 'left',
          minHeight: '28px',
          maxWidth: '100%',
          paddingTop: '1.5px',
          paddingBottom: '1.5px',

          '& + *': {
            marginTop: '14px',
          },

          '& > *:last-child': {
            transition: 'opacity 150ms ease-in-out',
          },
        },

        '& .MuiTypography-body1': {
          [muiTheme.breakpoints.down('sm')]: {
            lineHeight: '1em',
          },
        },

        '&:not([aria-selected="true"])': {
          opacity: 0.7,
        },

        '&.MuiTab-textColorInherit': {
          opacity: 1,
        },
      },
    },

    tabPanel: {
      '& > * + *': {
        marginTop: muiTheme.spacing(2),
      },
      animation: ' 150ms ease-out',
    },

    tabDescription: {
      ...muiTheme.typography.h5,

      [muiTheme.breakpoints.up('md')]: {
        marginTop: '16px',
      },

      '& p': {
        marginTop: 0,
        marginBottom: 0,

        '& + *': {
          marginTop: '16px',
        },
      },

      '& a': {
        ...muiTheme.typography.button,
        display: 'block',
      },

      '& > *:last-child': {
        [muiTheme.breakpoints.up('md')]: {
          marginBottom: '12px',
        },
      },
    },

    heading: {
      '& p, & h1, & h2, & h3, & h4, & h5, & h6': {
        ...muiTheme.typography.h4,
      },
    },

    rtc: {},

    link: {
      marginTop: muiTheme.spacing(4),
    },

    topBackground: {
      position: 'relative',
    },
  }),
  { name: 'GenCTLVerticalFeatured' },
);

export default useStyles;
